import type {
  AgentResearchEntitlement,
  AgentResearchJob,
  AgentResearchJobStatus,
  AgentResearchMaterializeChangeSetResponse,
  PersonalAgentDevice,
  PersonalAgentPresenceHint,
  PrivateAgentProfile,
  PrivateAgentPlacement,
  PrivateAgentReadiness
} from "@salesko/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import type { PrivateAgentListResponse } from "@salesko/contracts/private-agent-profile-projection";
import {
  AgentResearchApiError,
  cancelAgentResearchJob,
  createAgentResearchJob,
  fetchPrivateAgentReadiness,
  getAgentResearchEntitlement,
  getGraphFrame,
  getPrivateAgentProfile,
  listAgentResearchJobs,
  listPrivateAgents,
  listPersonalAgentDevices,
  listPersonalAgentPresenceHints,
  materializeAgentResearchChangeSet
} from "../../lib/api";
import { privateAgentReadinessQueryKey } from "./usePrivateAgentChat";

/**
 * Mirrors the API's own terminal set (`apps/api/src/agent-job-repository.ts`).
 * `waiting_device` is terminal on purpose: no paired computer accepted the
 * offer, the single active-job slot was released, and the owner re-triggers
 * after connecting a computer.
 */
const TerminalAgentResearchJobStatuses: readonly AgentResearchJobStatus[] = [
  "succeeded",
  "failed",
  "canceled",
  "waiting_device",
  "erased"
];

/** The statuses `POST /api/agent/research-jobs/:jobId/cancel` can still act on. */
const CancellableAgentResearchJobStatuses: readonly AgentResearchJobStatus[] = [
  "queued",
  "dispatching",
  "running"
];

export const PrivateAgentJobsPollIntervalMs = 10_000;

export function agentResearchJobIsTerminal(status: AgentResearchJobStatus): boolean {
  return TerminalAgentResearchJobStatuses.includes(status);
}

export function agentResearchJobIsCancellable(status: AgentResearchJobStatus): boolean {
  return CancellableAgentResearchJobStatuses.includes(status);
}

/**
 * Polling predicate. A research job only moves while something off-page is
 * working on it, so the list polls exactly while at least one job is
 * non-terminal and stops entirely otherwise — an all-terminal list can only
 * change through an action taken in this tab, which invalidates the query.
 */
export function privateAgentJobsRefetchInterval(
  items: ReadonlyArray<Pick<AgentResearchJob, "status">> | undefined
): number | false {
  if (!items || items.length === 0) {
    return false;
  }
  return items.some((item) => !agentResearchJobIsTerminal(item.status)) ? PrivateAgentJobsPollIntervalMs : false;
}

export type CreateAgentResearchJobVariables = {
  agentId: string;
  /**
   * Idempotency key for one trigger intent. Minted by the caller when the user
   * asks for research, so retrying the same mutation reuses the same key and the
   * API de-duplicates instead of creating a second job.
   */
  clientRequestId: string;
  /** Committed frame the proposal will be based on. */
  frameRecordId: string;
  question: string;
  targetCanonicalEntityId: string;
};

/**
 * A client-side precondition that stops a create before any request is made.
 * Deliberately not an `AgentResearchApiError`: nothing about it came from the
 * API, and a synthetic status would read as server state.
 */
export class PrivateAgentResearchPreconditionError extends Error {
  readonly reason: "frame_version_unavailable";

  constructor(reason: "frame_version_unavailable") {
    super(`Private Agent research precondition failed: ${reason}`);
    this.name = "PrivateAgentResearchPreconditionError";
    this.reason = reason;
  }
}

export type PrivateAgentJobsState = {
  agents: PrivateAgentListResponse["agents"];
  agentsError: unknown;
  refreshAgents: () => void;
  placement: PrivateAgentPlacement | null;
  readinessError: unknown;
  cancelJob: (jobId: string) => void;
  cancelPending: boolean;
  cancelingJobId: string | null;
  createError: unknown;
  createJob: (variables: CreateAgentResearchJobVariables) => void;
  createPending: boolean;
  devices: PersonalAgentDevice[];
  entitlement: AgentResearchEntitlement | null;
  jobs: AgentResearchJob[];
  jobsError: unknown;
  jobsLoading: boolean;
  materializeError: unknown;
  materializeJob: (jobId: string) => void;
  materializePending: boolean;
  materializingJobId: string | null;
  presenceHints: PersonalAgentPresenceHint[];
  profile: PrivateAgentProfile | null;
  /**
   * The server-computed chat admission verdict, or null while it has not been
   * read. It is never derived here from presence, devices, or profile.
   */
  readiness: PrivateAgentReadiness | null;
  /** The explicitly selected Agent does not exist. */
  profileMissing: boolean;
  profileLoading: boolean;
};

export type UsePrivateAgentJobsOptions = {
  agentId: string | undefined;
  enabled: boolean;
  /**
   * Called with the materialize receipt so the host can hand the pending change
   * set to the existing review flow. Materializing never applies anything.
   */
  onChangeSetMaterialized: (response: AgentResearchMaterializeChangeSetResponse) => void;
};

/**
 * Everything the Private Agent product surface reads and writes.
 *
 * Device truth, presence, entitlement and the agent profile each keep their one
 * existing source; this hook only composes them so the jobs panel and the
 * detail-panel trigger agree about why an action is unavailable.
 */
export function usePrivateAgentJobs({ agentId, enabled, onChangeSetMaterialized }: UsePrivateAgentJobsOptions): PrivateAgentJobsState {
  const queryClient = useQueryClient();
  const agentsQuery = useQuery({ queryKey: ["private-agents"], queryFn: listPrivateAgents, enabled, retry: false, staleTime: 30_000 });

  const jobsQuery = useQuery({
    queryKey: ["agent-research-jobs", agentId],
    queryFn: () => listAgentResearchJobs(agentId ?? "", 20),
    enabled: enabled && Boolean(agentId),
    retry: false,
    refetchInterval: (query) => privateAgentJobsRefetchInterval(query.state.data?.items)
  });

  const profileQuery = useQuery({
    queryKey: ["private-agent-profile", agentId],
    queryFn: () => getPrivateAgentProfile(agentId ?? ""),
    enabled: enabled && Boolean(agentId),
    // 404 is "no Private Agent yet" — a state the panel renders, not a failure.
    retry: false,
    staleTime: 30_000
  });

  const devicesQuery = useQuery({
    queryKey: ["personal-agent-devices"],
    queryFn: listPersonalAgentDevices,
    enabled,
    retry: 1,
    staleTime: 15_000
  });

  const presenceQuery = useQuery({
    queryKey: ["personal-agent-presence-hints"],
    queryFn: listPersonalAgentPresenceHints,
    enabled,
    retry: 1,
    staleTime: 10_000
  });

  // Same read cadence as the presence hint it supersedes for admission, but a
  // refused read must not retry: a 401/404 here is an answer, not a blip.
  const readinessQuery = useQuery({
    queryKey: privateAgentReadinessQueryKey(agentId, profileQuery.data?.placement.placementRevision),
    queryFn: () => fetchPrivateAgentReadiness(agentId ?? ""),
    enabled: enabled && Boolean(agentId && profileQuery.data && !profileQuery.isError),
    retry: false,
    // A blocked verdict is the only one that can change without a user action
    // on this surface, so it re-reads itself until the authority admits again.
    refetchInterval: (query) => (query.state.data?.chat === "blocked" ? 15_000 : false),
    staleTime: 10_000
  });

  const entitlementQuery = useQuery({
    queryKey: ["agent-research-entitlement"],
    queryFn: getAgentResearchEntitlement,
    enabled,
    retry: false,
    staleTime: 60_000
  });

  const placement = enabled && !profileQuery.isError ? profileQuery.data?.placement ?? null : null;
  useEffect(() => {
    if (readinessQuery.data && placement && readinessQuery.data.placementRevision !== placement.placementRevision) {
      void queryClient.invalidateQueries({ queryKey: ["private-agent-profile", agentId] });
      void queryClient.invalidateQueries({ queryKey: ["private-agents"] });
    }
  }, [agentId, placement, readinessQuery.data, queryClient]);

  const createMutation = useMutation({
    mutationFn: async (variables: CreateAgentResearchJobVariables) => {
      // The API rejects a create whose `expectedFrameVersion` is not the frame's
      // current version, so the version is read here rather than cached: there
      // is exactly one authority for it and it is the frame record itself.
      const record = await getGraphFrame(variables.frameRecordId);
      if (record.version === undefined) {
        throw new PrivateAgentResearchPreconditionError("frame_version_unavailable");
      }
      return createAgentResearchJob({
        agentId: variables.agentId,
        clientRequestId: variables.clientRequestId,
        expectedFrameVersion: record.version,
        frameRecordId: variables.frameRecordId,
        question: variables.question,
        targetCanonicalEntityId: variables.targetCanonicalEntityId
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["agent-research-jobs"] });
    }
  });

  const cancelMutation = useMutation({
    mutationFn: (jobId: string) => cancelAgentResearchJob(jobId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["agent-research-jobs"] });
    }
  });

  const materializeMutation = useMutation({
    mutationFn: (jobId: string) => materializeAgentResearchChangeSet(jobId),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ["agent-research-jobs"] });
      onChangeSetMaterialized(response);
    }
  });

  return {
    agents: enabled ? agentsQuery.data?.agents ?? [] : [],
    agentsError: agentsQuery.error ?? profileQuery.error,
    refreshAgents: () => { void agentsQuery.refetch(); if (agentId) void profileQuery.refetch(); if (placement) void readinessQuery.refetch(); },
    placement,
    readinessError: profileQuery.error ?? readinessQuery.error,
    cancelJob: (jobId) => cancelMutation.mutate(jobId),
    cancelPending: cancelMutation.isPending,
    cancelingJobId: cancelMutation.isPending ? cancelMutation.variables ?? null : null,
    createError: createMutation.isError ? createMutation.error : null,
    createJob: (variables) => createMutation.mutate(variables),
    createPending: createMutation.isPending,
    devices: devicesQuery.data?.devices ?? [],
    entitlement: entitlementQuery.data?.entitlement ?? null,
    jobs: enabled && agentId ? jobsQuery.data?.items ?? [] : [],
    jobsError: jobsQuery.isError ? jobsQuery.error : null,
    jobsLoading: jobsQuery.isLoading,
    materializeError: materializeMutation.isError ? materializeMutation.error : null,
    materializeJob: (jobId) => materializeMutation.mutate(jobId),
    materializePending: materializeMutation.isPending,
    materializingJobId: materializeMutation.isPending ? materializeMutation.variables ?? null : null,
    presenceHints: presenceQuery.data?.presenceHints ?? [],
    profile: enabled && !profileQuery.isError ? profileQuery.data?.profile ?? null : null,
    readiness: enabled && !readinessQuery.isError && readinessQuery.data?.agentId === agentId && readinessQuery.data?.placementRevision === placement?.placementRevision ? readinessQuery.data ?? null : null,
    profileLoading: profileQuery.isLoading,
    profileMissing: Boolean(agentId) && profileQuery.isError && isNotFound(profileQuery.error)
  };
}

function isNotFound(error: unknown) {
  return error instanceof AgentResearchApiError && error.status === 404;
}

/**
 * Minted once per trigger intent by the caller, never inside `mutationFn`:
 * a retry of the same mutation must carry the same idempotency key.
 */
export function newAgentResearchClientRequestId() {
  return `agent-research:${globalThis.crypto.randomUUID()}`;
}
